/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useMemo } from 'react';

export interface ICoveragePaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    resources: {
        previousAria: string;
        nextAria: string;
        pageAriaTemplate: string;
    };
}

function getPagesToRender(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    if (currentPage <= 4) {
        return [1, 2, 3, 4, 5, 'ellipsis', totalPages];
    }

    if (currentPage >= totalPages - 3) {
        return [1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
}

export const CoveragePagination: React.FC<ICoveragePaginationProps> = ({ currentPage, totalPages, onPageChange, resources }) => {
    const clampedTotalPages = Math.max(1, totalPages);

    const pageItems = useMemo(() => {
        return getPagesToRender(currentPage, clampedTotalPages);
    }, [currentPage, clampedTotalPages]);

    if (clampedTotalPages <= 1) {
        return null;
    }

    const previousDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= clampedTotalPages;

    return (
        <nav className='ms-search-result-container__pagination ms-coverage-pagination'>
            <ul className='msc-pagination'>
                <li className={`previous msc-page-item${previousDisabled ? ' disabled' : ''}`}>
                    <button
                        type='button'
                        className='msc-page-link'
                        aria-label={resources.previousAria}
                        aria-disabled={previousDisabled}
                        disabled={previousDisabled}
                        onClick={() => onPageChange(currentPage - 1)}
                    >
                        <span className='msc-pagination__prev'>
                            <span className='ms-search-result__pagination-left' aria-hidden='true' />
                            <span className='prev-text'>{resources.previousAria}</span>
                        </span>
                    </button>
                </li>

                {pageItems.map((page, index) => {
                    if (page === 'ellipsis') {
                        return (
                            <li key={`ellipsis-${index}`} className='msc-page-item ms-coverage-pagination__ellipsis'>
                                <span className='msc-page-link'>...</span>
                            </li>
                        );
                    }

                    const isActive = page === currentPage;
                    const pageAriaLabel = resources.pageAriaTemplate.replace('{0}', String(page));

                    return (
                        <li key={`page-${page}`} className={`msc-page-item${isActive ? ' active' : ''}`}>
                            <button
                                type='button'
                                className='msc-page-link'
                                aria-label={pageAriaLabel}
                                aria-current={isActive ? 'page' : undefined}
                                aria-disabled={isActive}
                                disabled={isActive}
                                onClick={() => onPageChange(page)}
                            >
                                {page}
                            </button>
                        </li>
                    );
                })}

                <li className={`next msc-page-item${nextDisabled ? ' disabled' : ''}`}>
                    <button
                        type='button'
                        className='msc-page-link'
                        aria-label={resources.nextAria}
                        aria-disabled={nextDisabled}
                        disabled={nextDisabled}
                        onClick={() => onPageChange(currentPage + 1)}
                    >
                        <span className='msc-pagination__next'>
                            <span className='next-text'>{resources.nextAria}</span>
                            <span className='ms-search-result__pagination-right' aria-hidden='true' />
                        </span>
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default CoveragePagination;
